/**
 * TASKS.WIZARD.ROUTE
 * Onboarding for the action plan as manager
 */
import { connect } from "react-redux"
import { ActionPlanState } from "@/redux/action-plan.types"
import { Session } from "@/redux/_session.types"
import { DashboardFilters, FilterState } from "@/redux/filter.types"
import { WithTranslation, withTranslation } from "react-i18next"
import Modal from "@/components/modal"
import BubbleText from "@/components/BubbleText"
import { useEffect, useState } from "react"
import Button from "@/components/button"
import { SurveyState } from "@/redux/survey.types"
import { faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons"
import { TopicState } from "@/redux/topic.types"
import { fetchTopics, fetchTopicsPublic } from "@/redux/_archive.actions"
import { store } from "@/core/root-reducer"
import LoadingModal from "@/modals/loading.modal"
import { keyBy } from "lodash"
import { topicGet } from "@/redux/topic.actions"
import { Socket } from "socket.io-client"
import { SocketCreate, SocketEmit } from "@/utils/socketio.utils"
import { actionPlanAddTask } from "@/redux/action-plan.actions"
import { useNavigate } from "react-router-dom"

interface StateProps extends WithTranslation {
  _session: Session
  actionPlan: ActionPlanState
  filter: FilterState
  survey: SurveyState
  topic: TopicState
}

function TasksWizardRoute(props: StateProps) {
  const navigate = useNavigate()
  const { t } = props

  const [isLoading, setIsLoading] = useState(true)

  const [step, setStep] = useState<"intro" | "choice">("intro")
  const [displayNextStep, setDisplayNextStep] = useState(false)

  function getBubbleText() {
    if (step === "intro" && props._session.userRole === "ADMIN") {
      return t("tasks_wizard_intro_all")
    }

    return t("tasks_wizard_" + step)
  }

  //Suggest 3 topics
  useEffect(() => {
    load()

    async function load() {
      //Load topics
      const allTopics = props.topic.list
      const managerTopics = await store.dispatch(
        fetchTopics(
          new DashboardFilters(),
          props._session.accountOptions.topicRepartitionCeil,
          false,
          props.survey.active.id,
          props.survey.active.dateStart
        )
      )

      const companyTopics = await store.dispatch(fetchTopicsPublic(props._session.accountId))

      if (managerTopics.error || companyTopics.error) {
        setIsLoading(false)
        return null
      }

      // Indexation des companyTopics par aid
      const companyByAid = keyBy(companyTopics, "aid")

      // Filtrer, mapper et trier and limit 3 topics
      const criticalTopics = managerTopics
        .filter((t) => t.note < 70)
        .map((t) => {
          const company = companyByAid[t.aid]
          const ecart = company ? t.note - company.note : t.note
          return { ...t, ecart }
        })
        .sort((a, b) => a.ecart - b.ecart)
        .slice(0, 5)

      if (criticalTopics.length) {
        setDisplayNextStep(true)
      }

      //Limit 3 topics
      allTopics.forEach((topic) => {
        topic.suggestTask = false
        const criticalTopic = criticalTopics.find((x) => x.aid === topic.aid)
        if (criticalTopic) {
          topic.suggestTask = true
        }
      })

      store.dispatch(topicGet(allTopics))
      setIsLoading(false)
    }
  }, [])

  //Trigger function to ask openAI
  async function askGpt<T = any>(prompt): Promise<T> {
    const socket: Socket = SocketCreate()
    return new Promise<T>((resolve, reject) => {
      SocketEmit(
        socket,
        "task-custom",
        {
          preprompt:
            "Tu dois répondre UNIQUEMENT avec un TABLEAU qui contient une liste de JSON valides, chaque ligne correspond à une tâche. Modèle de réponse : [{ id: string, title:string, text:string, topicAid:number }...]",
          prompt,
          sendMode: "all"
        },
        "fr",
        undefined,
        (res) => {
          try {
            const parsed = JSON.parse(res)

            // Si c'est déjà un tableau
            if (Array.isArray(parsed)) {
              return resolve(parsed as T)
            }

            // Si c'est un objet { something: [...] }
            if (parsed && typeof parsed === "object") {
              const firstKey = Object.keys(parsed)[0]
              const firstValue = firstKey ? parsed[firstKey] : null

              if (Array.isArray(firstValue)) {
                return resolve(firstValue as T)
              }
            }

            console.warn("GPT response is not an array, fallback []")
            return resolve([] as T)
          } catch (e) {
            console.warn("Invalid JSON from GPT, fallback []", e)
            resolve([] as T)
          }
        },
        (err) => {
          setIsLoading(false)
          reject(err)
        },
        () => {}
      )
    })
  }

  async function handleNext(withAssistant: boolean) {
    if (withAssistant) {
      setIsLoading(true)
      let prompt = "En tant que manager de la société " + props._session.accountName
      if (props._session.accountOptions.sector)
        prompt = prompt + " (secteur : " + props._session.accountOptions.sector + ") "

      if (props.filter.observerAttributes.length) {
        prompt = prompt + " et des services suivants : " + props.filter.observerAttributes.map((x) => x.name) + ". "
      }

      prompt =
        prompt +
        ", peux me faire pour chaque thème suivant une suggestion de tâche à intégrer dans un plan d'action (avec un titre, un texte et le topic associé). Il ne faut que 3 tasks max en output - tu peux enlever ce qui n'est pas pertinent (ou le cas ou un manager d'équipe ne peut pas intervenir - ex la rémunération ou la mutuelle). Format attendu : tableau JSON."
      prompt = prompt + "\n\n"
      prompt = prompt + "Voici la liste des topics et de leurs questions associées :\n\n"

      props.topic.list
        .filter((x) => x.suggestTask)
        .forEach((topic) => {
          prompt =
            prompt +
            "- " +
            topic.label +
            " (topicAid : " +
            topic.aid +
            ")" +
            ", question : " +
            topic.questionPrimary?.label +
            "\n"
        })

      const response = await askGpt(prompt)
      if (response?.length > 0) {
        for (let i = 0; i < response.length; i++) {
          const task = response[i]
          store.dispatch(actionPlanAddTask(task.topicAid, task.title, task.text, props._session.userName))
          await new Promise((resolve) => setTimeout(resolve, 250))
        }
      }

      setIsLoading(false)
    }

    navigate("/tasks/edit")
  }

  return (
    <Modal
      displayOwl
      title={t("tasks_wizard_title", { survey: props.survey.active.name })}
      displayOwlTop={72}
      footer={
        step === "intro" ? (
          <Button
            isFullWidth
            onClick={() => (displayNextStep ? setStep("choice") : handleNext(false))}
            className="primary"
            isWithBorder
            isLarge>
            {t("utils_next")}
          </Button>
        ) : (
          <div className="flex tasks-wizard-buttons">
            <Button icon={faWandMagicSparkles} isWithBorder isFullWidth onClick={() => handleNext(true)} isLarge>
              {t("utils_yes")}
            </Button>
            <div className="w-10" />
            <Button isWithBorder isFullWidth className="primary" onClick={() => handleNext(false)} isLarge>
              {t("utils_no")}
            </Button>
          </div>
        )
      }>
      {isLoading && <LoadingModal />}

      <div className="relative flex flex-col items-start">
        <div className="mt-4 mb-10 ml-[120px] w-[480px] h-[120px]">
          <BubbleText text={getBubbleText()} />
        </div>
      </div>
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  actionPlan: state.actionPlan,
  filter: state.filter,
  survey: state.survey,
  topic: state.topic
})

export default withTranslation()(connect(mapStateToProps)(TasksWizardRoute))
